// Copyright (c) Bentley Systems
import * as React from "react";
import { setConfirmation } from "../../app/confirmation-slice";
import { ActionVm } from "../../Action/action-vm";
import { ActionParameterVm } from "../../Action/action-parameter-vm";
import { Parameter } from "../../Action/parameter";
import { getNodeVisibilityValue, toSafeString, TreeNodeDataWithRaw } from "../etree-utils";
import { useEtreeActionContext } from "../contexts/EtreeActionContext";
import type { RefreshReason } from "./useEtreeData";

type UseEtreeActionOrchestrationProps = {
  itemActionsMap: Map<number, ActionVm>;
  urlParameters: Parameter[];
  accessToken: string;
  treeContextKeyRef: React.MutableRefObject<string>;
  navigateToInfPanelFunction: (navUrl: string) => void;
  navigate: (navigateStr: string) => void;
  dispatch: (action: unknown) => void;
  selectedNodeIdRef: React.MutableRefObject<string | null>;
  expandedNodesRef: React.MutableRefObject<Record<string, boolean>>;
  setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  saveTreeViewState: (
    override?: {
      selectedNodeId?: string | null;
      expandedNodes?: Record<string, boolean>;
    },
    reason?: string
  ) => void;
  refreshTree: (searchTermOverride?: string, reason?: RefreshReason) => Promise<void>;
  onNodeExpanded: (nodeId: string, isExpanded: boolean) => Promise<void>;
};

type UseEtreeActionOrchestrationResult = {
  actionPendingNodes: Record<string, boolean>;
  executeNodeAction: (node: TreeNodeDataWithRaw, wasNodeExpandedOverride?: boolean) => void;
};

/**
 * Orchestrates node action execution with confirmation dialogs, parameter resolution,
 * and pending state management.
 *
 * Handles:
 * - Resolving which action to execute for a selected node
 * - Showing confirmation dialogs via Redux before execution
 * - Managing per-node pending state during async action execution
 * - Triggering tree refresh, navigation, or inline panel display after action completion
 *
 * @param treeData - Current flat/hierarchical tree data for node lookups
 * @param itemActionsMap - Map of actionId → ActionVm
 * @param urlParameters - URL parameters passed to action requests
 * @param accessToken - Bearer token for API authentication
 * @param sourceNodeId - ID of the currently source/selected node
 * @param treeContextKeyRef - Ref to the current tree context key for stale-request detection
 * @param navigateToInfPanelFunction - Callback to navigate to the inline information panel
 * @param navigate - React Router navigate function
 * @param dispatch - Redux dispatch function
 * @param selectedNodeIdRef - Ref tracking the currently selected node ID
 * @param expandedNodesRef - Ref tracking the current expanded nodes map
 * @param setSelectedNodeId - State setter for the selected node ID
 * @param restoreExpandedNodes - Restores expanded nodes to a given snapshot
 * @param saveTreeViewState - Persists current tree view state to sessionStorage
 * @param refreshTree - Triggers a full tree reload
 * @param onNodeExpanded - Handles node expand/collapse toggle
 * @param getNodePathIds - Returns the IDs of all ancestor nodes for a given node
 * @returns Object with `actionPendingNodes` map and `executeNodeAction` handler
 */
export const useEtreeActionOrchestration = ({
  itemActionsMap,
  urlParameters,
  accessToken,
  treeContextKeyRef,
  navigateToInfPanelFunction,
  navigate,
  dispatch,
  selectedNodeIdRef,
  expandedNodesRef,
  setSelectedNodeId,
  saveTreeViewState,
  refreshTree,
  onNodeExpanded,
}: UseEtreeActionOrchestrationProps): UseEtreeActionOrchestrationResult => {

  const { actionPendingNodes, setNodePending } = useEtreeActionContext();

  const executeNodeAction = React.useCallback(
    (node: TreeNodeDataWithRaw, wasNodeExpandedOverride?: boolean) => {
      const actionStartTreeContextKey = treeContextKeyRef.current;
      const wasNodeExpanded =
        wasNodeExpandedOverride ?? expandedNodesRef.current[node.id] === true;

      selectedNodeIdRef.current = node.id;
      setSelectedNodeId(node.id);
      saveTreeViewState(
        {
          selectedNodeId: node.id,
        },
        "node_action_start"
      );

      if (!node.actionIds?.length || !itemActionsMap.get(node.actionIds[0])) return;

      const origAction = itemActionsMap.get(node.actionIds[0]) as ActionVm;
      const clonedAction = new ActionVm(origAction);
      clonedAction.actionParameters = origAction.actionParameters.map((p) => {
        const param = Object.assign(new ActionParameterVm(), p);
        if (param.valueType === "F") {
          const fieldName = param.value || param.name;
          if (fieldName) {
            const fieldValue = getNodeVisibilityValue(node, fieldName);
            const normalizedValue = toSafeString(fieldValue);
            if (normalizedValue !== undefined) param.value = normalizedValue;
          }
        }
        if (param.valueType === "P") {
          const fieldName = param.value || param.name;
          if (fieldName) {
            param.value = urlParameters.find(
              (up: Parameter) =>
                up.name?.toLocaleLowerCase() === fieldName.toLocaleLowerCase()
            )?.value;
          }
        }
        return param;
      });

      const executeActionInVm = () => {
        if (typeof clonedAction.executeAction === "function") {
          void clonedAction.executeAction(
            () => setNodePending(node.id, true),
            () => setNodePending(node.id, false),
            {
              action: clonedAction,
              actionEndedFunction: async () => {
                if (treeContextKeyRef.current !== actionStartTreeContextKey) {
                  return;
                }

                await refreshTree(undefined, 'action');

                if (treeContextKeyRef.current !== actionStartTreeContextKey) {
                  return;
                }

                if (wasNodeExpanded) {
                  await onNodeExpanded(node.id, true);
                } else {
                  await onNodeExpanded(node.id, false);
                }

                selectedNodeIdRef.current = node.id;
                setSelectedNodeId(node.id);

                saveTreeViewState({ selectedNodeId: node.id }, "node_action_end");
              },
              navigateToInformationPanelFunction: navigateToInfPanelFunction,
            },
            (navigateStr: string) => navigate(navigateStr),
            accessToken,
            dispatch
          );
        }
      };

      if (clonedAction.hasConfirmation && clonedAction.isItemAction) {
        dispatch(
          setConfirmation({
            isConfirmationOpen: true,
            onOkAction: executeActionInVm,
            caption: clonedAction.confirmationText?.caption,
            text: clonedAction.confirmationText?.text,
            okText: clonedAction.confirmationText?.ok,
            cancelText: clonedAction.confirmationText?.cancel,
          })
        );
      } else {
        void executeActionInVm();
      }
    },
    [
      expandedNodesRef,
      selectedNodeIdRef,
      setSelectedNodeId,
      saveTreeViewState,
      itemActionsMap,
      urlParameters,
      setNodePending,
      refreshTree,
      onNodeExpanded,
      treeContextKeyRef,
      navigateToInfPanelFunction,
      navigate,
      accessToken,
      dispatch,
    ]
  );

  return {
    actionPendingNodes,
    executeNodeAction,
  };
};
